// @flow
import queryString from 'query-string';
import { titleCaseToCamelCase } from 'utils/string';
import { filterNulls } from 'utils/functional';
import { getJson, postJson, putJson, patchJson, del } from 'utils/http';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';
import type { Volop } from 'api/volopsAdmin';
import type { GradFairId } from 'api/gradSchools';
import type {
  BillingAddress,
  Address,
  AddressWithDescription,
  ImageData,
  SlideshowData,
  ImageWithCaption,
  Admin,
  AdminRequest,
  AdminInvite,
  UserMini,
  ReferrerCounts,
  RemoteOptions
} from './shared';

export type LineItem = {
  id: string,
  created: string,
  itemTypeId?: string,
  itemType: string,
  itemId?: string,
  listingId?: string,
  listingName?: string,
  description: string,
  quantity: number,
  unitPrice: number,
  due: string,
  invoice: {
    id: string,
    number: string
  }
};

export type LineItemWithPayment = {
  ...$Exact<LineItem>,
  paymentParts: Array<{
    id: string,
    created: string,
    amount: number,
    payment: {
      id: string,
      method: string,
      transactionId: string,
      amount: number
    }
  }>
};

export type Invoice = {
  id: string,
  number: number,
  created: string,
  updated: string,
  closed: string,
  billingAddress: BillingAddress,
  lineItems: Array<LineItemWithPayment>,
  creator: ?UserMini,
  paymentSettled: boolean,
  orgNote: string,
  vatNumber: ?string,
  due: string,
  totalForInvoice: string,
  acceptsPartialPayment: boolean,
  publicUrl: string,
  pendingPayment?: boolean
};

type InvoiceMini = {
  id: string,
  number: number,
  created: string,
  closed: string,
  paymentSettled: boolean
};

type ListingInvoice = {
  ...$Exact<InvoiceMini>,
  pendingPayment: boolean
};

export type Org = {
  id: string,
  name: string,
  description: string,
  email: string,
  phone: string,
  areasOfFocus: Array<string>,
  is501c3: boolean,
  orgType: string,
  created: string,
  isPublished: boolean,
  isApproved: boolean,
  isAllowedToPost: boolean,
  isFrozen: boolean,
  paysForListings: boolean,
  canPayByCheck: boolean,
  canPostVolops: boolean,
  canDeleteOrg: boolean,
  canAddAdmins: boolean,
  canAddOwners: boolean,
  hasInvoices: boolean,
  hasApi: boolean,
  hasAts: boolean,
  orgSize?: string,
  relatedJobAreas?: boolean,
  relatedJobCount?: number,
  clients?: string,
  services?: string,
  isBCorp?: boolean,
  beneficiariesPayingCustomers?: boolean,
  extraDetails?: string,
  canPostAnonymously: boolean,
  postingRequiresApproval: boolean,
  hasGradProgram: boolean,
  gradProgramLabel: ?'EXPIRES_SOON' | 'EXPIRED',
  locale: string,
  url: ?{ [UserLocale]: string },
  address: Address,
  governmentId: ?string,
  vatNumber: ?string,
  mustProvideVat: boolean,
  links: Array<string>,
  logo: ?ImageData,
  banner: ?ImageData,
  images: Array<ImageWithCaption>,
  featureFairRegistrations: boolean,
  featureFairLeads: boolean,
  fairContacts: number,
  admins: Array<Admin>,
  adminRequests: Array<AdminRequest>,
  invites: Array<AdminInvite>,
  invoices: Array<InvoiceMini>,
  frozenListings: Array<{
    id: string,
    name: string,
    type: string
  }>,
  itemsInCart: number,
  credits: {
    JOB: number,
    INTERNSHIP: number
  },
  promoCredits: {
    JOB: number,
    INTERNSHIP: number
  },
  searchApiKey: string,
  jazzhr: ?{
    customerId: string,
    autoPublish: boolean,
    byUser: ?{
      id: string,
      name: string,
      email: string
    }
  },
  greenhouse: ?{
    boardToken: string,
    autoPublish: boolean,
    byUser: ?{
      id: string,
      name: string,
      email: string
    }
  },
  prices: {
    listings: {
      JOB: number,
      INTERNSHIP: number
    },
    GRAD_PROGRAM: string,
    CREDIT_PACKS: {
      type: string,
      price: number,
      quantity: number
    }
  },
  tuitionCustomItems: Array<string>,
  admissionCustomItems: Array<string>,
  studentBodyCustomItems: Array<string>
};

export type Job = {
  type: 'JOB',
  id: string,
  name: string,
  locale: UserLocale,
  hasOrgNameOverride: boolean,
  orgNameOverride: ?string,
  isPostedAnonymously: boolean,
  status: Array<string>,
  created: string,
  updated: string,
  deleted: ?string,
  isEditable: boolean,
  isPublished: boolean,
  firstPublished: ?string,
  isDraft: boolean,
  isArchived: boolean,
  isFrozen: boolean,
  isHidden: boolean,
  canBeHidden: boolean,
  canBeUnhidden: boolean,
  canBeReposted: boolean,
  canBePosted: boolean,
  creator: ?UserMini,
  repostFrom: ?{
    id: string,
    name: string
  },
  description: string,
  slug: string,
  expires: string,
  address: Address,
  hasAts: boolean,
  requestedAttachments: Array<{ label: string }>,
  applyAck: ?string,
  applyEmail: ?string,
  applyUrl: ?string,
  applyText: ?string,
  benefits: ?string,
  functions: Array<string>,
  url: { [UserLocale]: string },
  invoice: ?ListingInvoice,
  applicationDeadline: ?string,
  remoteOptions: RemoteOptions,
  startDate: ?string,
  salaryMinimum: ?number,
  salaryMaximum: ?number,
  salaryDetails: ?string,
  professionalLevel: string,
  languageProficiency: ?string,
  education: string,
  isFullTime: boolean,
  isTemporary: boolean,
  isContract: boolean,
  applicationCount: number,
  uniqueViews: number,
  referrerCounts?: ReferrerCounts
};

export type Internship = {
  type: 'INTERNSHIP',
  id: string,
  name: string,
  locale: UserLocale,
  hasOrgNameOverride: boolean,
  orgNameOverride: ?string,
  isPostedAnonymously: boolean,
  status: Array<string>,
  created: string,
  updated: string,
  deleted: ?string,
  isEditable: boolean,
  isPublished: boolean,
  firstPublished: ?string,
  isDraft: boolean,
  isArchived: boolean,
  isFrozen: boolean,
  isHidden: boolean,
  canBeHidden: boolean,
  canBeUnhidden: boolean,
  canBeReposted: boolean,
  canBePosted: boolean,
  creator: ?UserMini,
  repostFrom: ?{
    id: string,
    name: string
  },
  description: string,
  slug: string,
  expires: string,
  address: Address,
  hasAts: boolean,
  requestedAttachments: Array<{ label: string }>,
  applyAck: ?string,
  applyEmail: ?string,
  applyUrl: ?string,
  applyText: ?string,
  benefits: ?string,
  functions: Array<string>,
  url: { [UserLocale]: string },
  invoice: ?ListingInvoice,
  applicationDeadline: ?string,
  remoteOptions: RemoteOptions,
  startDate: ?string,
  endDate: ?string,
  paid: boolean,
  payment: ?string,
  wage: ?string,
  academicCredits: boolean,
  paymentOk: boolean,
  hoursPerWeek: ?number,
  applicationCount: number,
  uniqueViews: number,
  referrerCounts?: ReferrerCounts
};

export type Listing = Job | Internship | Volop;

export type RepostedListing = {
  id: string,
  name: string,
  type: 'JOB' | 'INTERNSHIP' | 'VOLOP',
  firstPublished: string,
  location: string
};

export type Payment = {
  id: string,
  created: string,
  method: string,
  transactionId: string,
  amount: string,
  last4?: string,
  parts: Array<{
    id: string,
    created: string,
    amount: string,
    lineItem: LineItem
  }>
};

export type CreditPack = {
  id: string,
  created: string,
  packId: string,
  invoice: ?{
    id: string,
    number: string,
    paymentSettled: boolean
  },
  usedOn: string
};

export type Credit = {
  id: string,
  isUsed: boolean,
  isCancelled: boolean,
  invoice: ?{
    id: string,
    number: string
  },
  listing: ?{
    id?: string,
    name: string,
    listingType: string
  },
  usedBy: ?{
    id?: string,
    name?: string,
    email?: string,
    type: string
  },
  usedOn: ?string,
  usedByStaff: boolean
};

export type GradProgramUnpaidInvoice = {
  id: string,
  closed: string,
  creator: {
    id: string,
    name: string
  }
};

type TestimonialImage = ImageData;

export type Testimonial = {
  name: string,
  text: string,
  degree: string,
  year: string | number,
  image: TestimonialImage,
  id: ?string
};

export type GradProgram = {
  id: string,
  name: string,
  schoolName: string,
  description: string,
  whatsSpecial: string,
  phone: string,
  email: string,
  links: Array<string>,
  created: string,
  updated: string,
  expires: string,
  creator: {
    id: string,
    name: string,
    email: string
  },
  org: {
    id: string,
    orgType: string
  },
  isPublished: boolean,
  firstPublished: ?string,
  isAwaitingOrgApproval: boolean,
  address: Address,
  logo: ?ImageData,
  banner: ?ImageData,
  slideshowItems: Array<SlideshowData>,
  applicationGRE: boolean,
  applicationGMAT: boolean,
  applicationTOEFL: boolean,
  applicationIELTS: boolean,
  applicationPTE: boolean,
  applicationLettersRecomm2: boolean,
  applicationLettersRecomm3: boolean,
  applicationAcademicTranscripts: boolean,
  applicationPersonalStatement: boolean,
  additionalRequirements: string,
  nbOfStudents: number,
  maleStudents: number,
  femaleStudents: number,
  isHistoricallyBlack: boolean,
  hasDiversityOffice: boolean,
  isMinorityServing: boolean,
  associations: ?string,
  applicationFee: ?number,
  avgInStateTuition: ?number,
  avgOutStateTuition: ?number,
  workStudyAvailable: boolean,
  financialAidIntl: boolean,
  applicationFeeWaiver: boolean,
  avgFinancialAid: number,
  applicationDeadline: ?string,
  testimonials: Array<Testimonial>,
  isPublicSchool: boolean,
  acceptanceRate: ?number,
  url: ?{ [UserLocale]: string },
  unpaidInvoice: ?GradProgramUnpaidInvoice,
  tuitionCustomItems: Array<string>,
  studentBodyCustomItems: Array<string>,
  admissionCustomItems: Array<string>,
  canBeHidden: boolean,
  canBeUnhidden: boolean,
  isHidden: boolean,
  status: Array<string>,
  areasOfStudy: Array<string>,
  degreeProgramsOffered: Array<string>,
  fairLocations: Array<GradFairId>,
  savedCount: number
};

export type Contact = {
  created: string,
  firstName: string,
  lastName: string,
  email: string,
  interestedInDegreeProgram: string,
  expectedEnrollmentDate: string,
  notes: string,
  location: { id: string, name: string }
};

export type Integration = 'greenhouse' | 'jazzhr';

export type IntegrationJobStatus =
  | 'NOT_IMPORTED'
  | 'IMPORTED'
  | 'DONT_IMPORT'
  | 'FAILED';

export type IntegrationJob = {
  id: number,
  title: string,
  url: string,
  idealistJobId: string,
  status: IntegrationJobStatus
};

export type EventStatus =
  | 'ARCHIVED'
  | 'DELETED'
  | 'DRAFT'
  | 'FROZEN'
  | 'HIDDEN'
  | 'PUBLISHED'
  | 'PULLED';

export type IdealistDayEvent = {|
  id: string,
  status: $ReadOnlyArray<EventStatus>,
  name: string,
  description: string,
  slug: string,
  starts: number,
  localizedStarts: string,
  date: string,
  ends: string,
  localizedEnds: string,
  timezone: string,
  creator: {
    id: string,
    name: string,
    email: string
  },
  address: AddressWithDescription,
  url: ?{ [UserLocale]: string },
  rsvpEmail: ?string,
  rsvpUrl: ?string,
  rsvpText: ?string,
  directions: ?string,
  isPublished: boolean,
  firstPublished: ?string,
  isDraft: boolean,
  isArchived: boolean,
  archived: ?string,
  isFrozen: boolean,
  isHidden: boolean,
  isEditable: boolean,
  canBeHidden: boolean,
  canBeUnhidden: boolean,
  canBeReposted: boolean,
  canBePosted: boolean,
  repostFrom: ?{
    id: string,
    name: string
  },
  created: number,
  updated: number,
  deleted: ?number,
  isApproved: null,
  type: 'IDEALIST_DAY_EVENT'
|};

export type EmailNotificationPreferencesInfo = {
  frequency: string
};

export type AtsNotificationPreferencesInfo = {
  listingExpires: boolean,
  newApplication: boolean,
  newApplicationFrequency: string
};

export const fetchOrg = (orgId: string): Promise<Org> =>
  getJson(`/data/orgdashboard/${orgId}/organization`).then(({ org }) => org);

export const updateOrg = (orgInfo: Object): Promise<Org> =>
  putJson(`/data/orgdashboard/${orgInfo.id}/organization`, orgInfo).then(
    ({ org }) => org
  );

export const partialUpdateOrg = (orgInfo: Object): Promise<Org> =>
  patchJson(`/data/orgdashboard/${orgInfo.id}/organization`, orgInfo).then(
    ({ org }) => org
  );

export function deleteOrg(orgId: string) {
  return del(`/data/orgdashboard/${orgId}/organization`);
}

export function addOwner(
  orgId: string,
  email: string
): Promise<{ user: ?Admin, invite: ?AdminInvite }> {
  return postJson(`/data/orgdashboard/${orgId}/owners`, { email });
}

export function addAdmin(
  orgId: string,
  email: string
): Promise<{ user: ?Admin, invite: ?AdminInvite }> {
  return postJson(`/data/orgdashboard/${orgId}/admins`, { email });
}

export function removeAdmin(orgId: string, userId: string) {
  return del(`/data/orgdashboard/${orgId}/admins/${userId}`);
}

export function approveAdminRequest(orgId: string, requestId: string) {
  return postJson(`/data/orgdashboard/${orgId}/admin-requests/${requestId}`, {
    approved: true
  }).then(({ admin }) => admin);
}

export function rejectAdminRequest(orgId: string, requestId: string) {
  return postJson(`/data/orgdashboard/${orgId}/admin-requests/${requestId}`, {
    approved: false
  });
}

export const updateVatNumber = (orgId: string, vatNumber: string) =>
  putJson(`/data/orgdashboard/${orgId}/organization/vat`, { vatNumber });

export const deleteBillingAddress = (orgId: string, addressId: string) =>
  del(`/data/orgdashboard/${orgId}/billing-addresses/${addressId}`);

export function createJob(
  orgId: string,
  payload: Object
): Promise<{
  listings: Array<Object>,
  requiresPayment: boolean
}> {
  return postJson(`/data/orgdashboard/${orgId}/jobs`, payload).then(
    ({ jobs: listings, requiresPayment }) => ({
      listings,
      requiresPayment
    })
  );
}

export function validateJob(
  orgId: string,
  payload: Object
): Promise<{ job: Job }> {
  return postJson(`/data/orgdashboard/${orgId}/jobs-validation`, payload);
}

export function updateJob(
  orgId: string,
  payload: Object
): Promise<{ job: Job, requiresPayment: boolean, hasInvoice: true }> {
  return putJson(`/data/orgdashboard/${orgId}/jobs/${payload.id}`, payload);
}

export function createInternship(
  orgId: string,
  payload: Object
): Promise<{
  listings: Array<Object>,
  requiresPayment: boolean
}> {
  return postJson(`/data/orgdashboard/${orgId}/internships`, payload).then(
    ({ internships: listings, requiresPayment }) => ({
      listings,
      requiresPayment
    })
  );
}

export function validateInternship(
  orgId: string,
  payload: Object
): Promise<{ internship: Internship }> {
  return postJson(
    `/data/orgdashboard/${orgId}/internships-validation`,
    payload
  );
}

export function updateInternship(
  orgId: string,
  payload: Object
): Promise<{
  internship: Internship,
  requiresPayment: boolean,
  hasInvoice: true
}> {
  return putJson(
    `/data/orgdashboard/${orgId}/internships/${payload.id}`,
    payload
  );
}

export function createVolop(
  orgId: string,
  payload: Object
): Promise<{ volop: Volop }> {
  return postJson(`/data/orgdashboard/${orgId}/volops`, payload);
}

export function validateVolop(
  orgId: string,
  payload: Object
): Promise<{ volop: Volop }> {
  return postJson(`/data/orgdashboard/${orgId}/volops-validation`, payload);
}

export function updateVolop(
  orgId: string,
  payload: Object
): Promise<{ volop: Volop }> {
  return putJson(`/data/orgdashboard/${orgId}/volops/${payload.id}`, payload);
}

export function fetchListing(
  orgId: string,
  listingType: string,
  listingId: string
): Promise<Job> {
  return getJson(
    `/data/orgdashboard/${orgId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function hideListing({
  orgId,
  listingId,
  listingType
}: {
  orgId: string,
  listingId: string,
  listingType: string
}) {
  return postJson(
    `/data/orgdashboard/${orgId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/hide`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function unhideListing({
  orgId,
  listingId,
  listingType
}: {
  orgId: string,
  listingId: string,
  listingType: string
}) {
  return postJson(
    `/data/orgdashboard/${orgId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/unhide`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function publishListing({
  orgId,
  listingId,
  listingType
}: {
  orgId: string,
  listingId: string,
  listingType: string
}) {
  return postJson(
    `/data/orgdashboard/${orgId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/post`
  ).then(response => ({
    listing: response[titleCaseToCamelCase(listingType)],
    requiresPayment: response.requiresPayment,
    hasInvoice: response.hasInvoice
  }));
}

export function deleteListing({
  orgId,
  listingId,
  listingType
}: {
  orgId: string,
  listingId: string,
  listingType: string
}) {
  return del(
    `/data/orgdashboard/${orgId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function fetchRepostedListings(
  orgId: string
): Promise<Array<RepostedListing>> {
  return getJson(`/data/orgdashboard/${orgId}/published-reposts`).then(
    ({ listings }) => listings
  );
}

export const fetchInvoices = (
  orgId: string,
  filters: {
    number: ?string,
    before: ?string,
    after: ?string,
    outstandingOnly?: boolean,
    pageIndex: number,
    perPage: number
  }
): Promise<{
  invoices: Array<Invoice>,
  count: number,
  perPage: number,
  nbPages: number
}> => {
  const {
    number,
    before,
    after,
    outstandingOnly,
    pageIndex,
    perPage
  } = filters;

  const params = {
    number,
    before,
    after,
    outstanding: !!outstandingOnly,
    page: pageIndex,
    perPage
  };

  return getJson(
    `/data/orgdashboard/${orgId}/invoices?${queryString.stringify(params)}`
  );
};

export const fetchOpenInvoices = (orgId: string): Promise<Array<Invoice>> =>
  getJson(`/data/orgdashboard/${orgId}/open-invoices`).then(
    ({ invoices }) => invoices
  );

export function fetchInvoice(
  orgId: string,
  invoiceId: string
): Promise<Invoice> {
  return getJson(`/data/orgdashboard/${orgId}/invoices/${invoiceId}`).then(
    ({ invoice }) => invoice
  );
}

export function updateInvoiceOrgNote(
  orgId: string,
  invoiceId: string,
  orgNote: string
): Promise<void> {
  return putJson(`/data/orgdashboard/${orgId}/invoices/${invoiceId}/org-note`, {
    orgNote
  });
}

export function updateInvoiceBillingAddress(
  orgId: string,
  invoiceId: string,
  payload: Object
): Promise<void> {
  return putJson(
    `/data/orgdashboard/${orgId}/invoices/${invoiceId}/billing-address`,
    payload
  );
}

export function payInvoiceByCheck(
  orgId: string,
  invoiceId: string
): Promise<Invoice> {
  return postJson(`/data/orgdashboard/${orgId}/pay-by-check`, {
    invoiceIds: [invoiceId]
  });
}

export function payInvoicesByCreditCard(
  orgId: string,
  payload: Object
): Promise<BackgroundTask> {
  return postJson(
    `/data/orgdashboard/${orgId}/pay-by-credit-card`,
    payload
  ).then(({ task }) => task);
}

export function payLineItemsWithCredits(
  orgId: string,
  lineItemIds: Array<string>
): Promise<Invoice> {
  return postJson(`/data/orgdashboard/${orgId}/use-credits`, {
    lineItemIds
  });
}

export const removeLineItem = (
  orgId: string,
  lineItemId: string
): Promise<Invoice> =>
  del(`/data/orgdashboard/${orgId}/line-items/${lineItemId}`);

export const fetchBillingAddresses = (
  orgId: string
): Promise<Array<BillingAddress>> =>
  getJson(`/data/orgdashboard/${orgId}/billing-addresses`).then(
    ({ billingAddresses }) => billingAddresses
  );

export function updateCheckoutBillingAddress(
  orgId: string,
  payload: Object
): Promise<void> {
  return postJson(
    `/data/orgdashboard/${orgId}/checkout/billing-address`,
    payload
  );
}

export const fetchPayments = (
  orgId: string,
  filters: {
    before: ?string,
    after: ?string,
    pageIndex: number,
    perPage: number
  }
): Promise<{
  payments: Array<Payment>,
  count: number,
  perPage: number,
  nbPages: number
}> => {
  const { before, after, pageIndex, perPage } = filters;

  const params = {
    before,
    after,
    page: pageIndex,
    perPage
  };

  return getJson(
    `/data/orgdashboard/${orgId}/payments?${queryString.stringify(params)}`
  );
};

export function fetchPayment(
  orgId: string,
  paymentId: string
): Promise<Payment> {
  return getJson(`/data/orgdashboard/${orgId}/payments/${paymentId}`).then(
    ({ payment }) => payment
  );
}

export function fetchPaymentSummary(orgId: string): Promise<Object> {
  return getJson(`/data/orgdashboard/${orgId}/payment-summary`).then(
    ({ report }) => report
  );
}

export const fetchCreditPacks = (orgId: string): Promise<Array<CreditPack>> =>
  getJson(`/data/orgdashboard/${orgId}/credits`).then(({ packs }) => packs);

export const fetchCreditPack = (
  orgId: string,
  packId: string
): Promise<{ pack: CreditPack, credits: Array<Credit> }> =>
  getJson(`/data/orgdashboard/${orgId}/credits/${packId}`);

export const fetchCreditsForPurchase = (
  orgId: string
): Promise<Array<Object>> =>
  getJson(`/data/orgdashboard/${orgId}/credits-for-purchase`).then(
    ({ forPurchase }) => forPurchase
  );

export const fetchGradProgram = (orgId: string): Promise<GradProgram> =>
  getJson(`/data/orgdashboard/${orgId}/grad-program`).then(
    ({ gradProgram }) => gradProgram
  );

export function createGradProgram(
  orgId: string,
  programInfo: Object
): Promise<GradProgram> {
  return postJson(`/data/orgdashboard/${orgId}/grad-program`, programInfo).then(
    ({ gradProgram }) => gradProgram
  );
}

export function updateGradProgram(
  orgId: string,
  programInfo: Object
): Promise<GradProgram> {
  return putJson(`/data/orgdashboard/${orgId}/grad-program`, programInfo).then(
    ({ gradProgram }) => gradProgram
  );
}

export function fetchGradFairContacts(
  orgId: string,
  page: number
): Promise<{ contacts: Array<Contact>, lastPage: number, perPage: number }> {
  // 1-indexed pages
  const url = `/data/orgdashboard/${orgId}/gradfair/contacts?page=${page}`;
  return getJson(url);
}

export function getIntegrationJobs(
  orgId: string,
  type: Integration
): Promise<{ jobs: Array<IntegrationJob>, hasError: boolean }> {
  const url = `/data/orgdashboard/${orgId}/${type}/jobs`;
  return getJson(url);
}

export function fetchGradProgramContacts(
  orgId: string,
  { page, start, end }: { page: number, start: string, end: string } = {}
): Promise<{
  contacts: Array<Contact>,
  lastPage: number,
  pageIndex: number,
  count: number,
  perPage: number
}> {
  const params = queryString.stringify(filterNulls({ page, start, end }));
  const url = `/data/orgdashboard/${orgId}/grad-program/contacts?${params}`;
  return getJson(url);
}

export function fetchGradProgramContactsCsvUrl(
  orgId: string,
  { start, end }: { start: string, end: string } = {}
): string {
  const params = queryString.stringify(filterNulls({ start, end }));
  return `/data/orgdashboard/${orgId}/grad-program/contacts.csv?${params}`;
}

export function fetchLeadsNotificationPreferences(
  orgId: string
): Promise<string> {
  const url = `/data/orgdashboard/${orgId}/grad-program/notification-preferences`;
  return getJson(url).then(({ frequency }) => frequency);
}

export function updateLeadsNotificationPreferences(
  orgId: string,
  emailNotificationPreferencesInfo: EmailNotificationPreferencesInfo
): Promise<string> {
  return putJson(
    `/data/orgdashboard/${orgId}/grad-program/notification-preferences`,
    emailNotificationPreferencesInfo
  ).then(({ frequency }) => frequency);
}

export function fetchAtsNotificationPreferences(
  orgId: string
): Promise<AtsNotificationPreferencesInfo> {
  const url = `/data/orgdashboard/${orgId}/ats/notification-preferences`;
  return getJson(url).then(({ preferences }) => preferences);
}

export function updateAtsListingExpiresNotificationPreference(
  orgId: string,
  listingExpires: boolean
): Promise<AtsNotificationPreferencesInfo> {
  return putJson(
    `/data/orgdashboard/${orgId}/ats/notification-preferences/listing-expires`,
    { listingExpires }
  ).then(({ preferences }) => preferences);
}

export function updateAtsNewApplicationNotificationPreference(
  orgId: string,
  newApplication: boolean
): Promise<AtsNotificationPreferencesInfo> {
  return putJson(
    `/data/orgdashboard/${orgId}/ats/notification-preferences/new-application`,
    { newApplication }
  ).then(({ preferences }) => preferences);
}

export function updateAtsNewApplicationFrequencyNotificationPreference(
  orgId: string,
  newApplicationFrequency: string
): Promise<AtsNotificationPreferencesInfo> {
  return putJson(
    `/data/orgdashboard/${orgId}/ats/notification-preferences/new-application-frequency`,
    { newApplicationFrequency }
  ).then(({ preferences }) => preferences);
}

export function fetchEvent(
  orgId: string,
  eventId: string
): Promise<IdealistDayEvent> {
  return getJson(
    `/data/orgdashboard/${orgId}/idealist-day-events/${eventId}`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function createEvent(
  orgId: string,
  eventInfo: Object
): Promise<IdealistDayEvent> {
  return postJson(
    `/data/orgdashboard/${orgId}/idealist-day-events`,
    eventInfo
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function updateEvent(
  orgId: string,
  eventId: string,
  eventInfo: Object
): Promise<IdealistDayEvent> {
  return putJson(
    `/data/orgdashboard/${orgId}/idealist-day-events/${eventId}`,
    eventInfo
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function publishEvent(orgId: string, eventId: string) {
  return postJson(
    `/data/orgdashboard/${orgId}/idealist-day-events/${eventId}/post`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function hideEvent(orgId: string, eventId: string) {
  return postJson(
    `/data/orgdashboard/${orgId}/idealist-day-events/${eventId}/hide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function unhideEvent(orgId: string, eventId: string) {
  return postJson(
    `/data/orgdashboard/${orgId}/idealist-day-events/${eventId}/unhide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function deleteEvent(orgId: string, eventId: string) {
  return del(`/data/orgdashboard/${orgId}/idealist-day-events/${eventId}`).then(
    ({ idealistDayEvent }) => idealistDayEvent
  );
}

export function fetchOrgEvents(
  orgId: string
): Promise<Array<IdealistDayEvent>> {
  return getJson(`/data/orgdashboard/${orgId}/idealist-day-events`).then(
    ({ events }) => events
  );
}

export function hideGradProgram({ orgId }: { orgId: string }) {
  return postJson(`/data/orgdashboard/${orgId}/grad-program/hide`).then(
    ({ gradProgram }) => gradProgram
  );
}

export function unhideGradProgram({ orgId }: { orgId: string }) {
  return postJson(`/data/orgdashboard/${orgId}/grad-program/unhide`).then(
    ({ gradProgram }) => gradProgram
  );
}
